import { Subject, BehaviorSubject, fromEvent } from 'rxjs';
import { map, distinctUntilChanged, mergeMap, filter, debounceTime } from 'rxjs/operators';
import { FromEventTarget } from 'rxjs/internal/observable/fromEvent';
import { IMessageBus, sendMessage } from 'q2-tecton-common/lib/utility/message-bus';
import { DSCustomElement } from 'dry-suite';
import { ResolvedTab, TectonResolvedTab } from '../../types';
import { IDict } from '../../types/utility';
import {
    buildLoaders,
    ITectonOutletAdaptorResponse,
    setupOutletIFrameLoading,
    setupOutletIFrameResizer,
    setupOutletSetTitleListener,
    setupOutletInfo
} from 'q2-tecton-common/lib/outlets/tecton-outlet-base';
import { ITranslationSetup } from '../t';
import { Maybe } from 'maybe-not';
import addParamToURL from 'q2-tecton-common/lib/utility/addParamToURL';
import { checkRequiredPermissionsOnPlatform } from 'q2-tecton-common/lib/utility/check-permissions';

export const tectonTabbedOutletObservedAttrs = [
    { name: 'context', reflectAs: String },
    { name: 'value', reflectAs: String },
    { name: 'contextValue', reflectAs: String }
];

export interface ITectonTabPane extends HTMLElement {
    provided: boolean;
    label: string;
    value: string;
    selected: boolean;
    url: string;
    showForm: boolean;
    authPayload?: { key: string; value: string }[];
    name?: string;
    moduleId?: string;
    minHeight?: string;
}

export function TectonTabbedOutlet(loc: ITranslationSetup['tFn']) {
    return (
        messageBus: IMessageBus,
        outletPath: string,
        customElement: DSCustomElement,
        memorizedProps$: BehaviorSubject<IDict<any>>,
        resize?: () => void
    ): ITectonOutletAdaptorResponse => {
        const wrapperEl = document.createElement('div');
        wrapperEl.appendChild(document.createElement('slot'));
        const resolvedTabs$ = new BehaviorSubject<
            [ResolvedTab[], { key: string; value: string }[]]
        >([[], []]);
        const settled$ = new BehaviorSubject(false);
        const getContent = function() {
            const tabPaneRoot = customElement.querySelector('tecton-tab-pane[selected]')!;
            return {
                iframeEl: tabPaneRoot.shadowRoot!.querySelector('iframe')!,
                loaderWrapper: tabPaneRoot.querySelector<HTMLDivElement>(
                    "div[slot='loading-wrapper']"
                )!,
                minHeight: memorizedProps$.getValue().minHeight
            };
        };

        const getModuleId = function() {
            const selectedTabPane = customElement.querySelector(
                'tecton-tab-pane[selected]'
            )! as ITectonTabPane;
            return selectedTabPane.moduleId;
        };

        const loadingSubscription = setupOutletIFrameLoading(messageBus, getModuleId, getContent);
        const resizerSubscription = setupOutletIFrameResizer(
            messageBus,
            getModuleId,
            getContent,
            resize
        );
        const titleSubscription = setupOutletSetTitleListener(messageBus, getModuleId, getContent);
        const outletInfoSubscription = setupOutletInfo(messageBus, getModuleId, getContent);

        const teardown$ = new BehaviorSubject(() => {
            loadingSubscription.unsubscribe();
            resizerSubscription.unsubscribe();
            titleSubscription.unsubscribe();
            outletInfoSubscription.unsubscribe();
        });

        const memorizedPropsSubscription = memorizedProps$
            .pipe(
                map(props => {
                    return {
                        contextValue: props.contextValue as string,
                        resolvedType: props.resolvedType as string
                    };
                }),
                filter(({ contextValue, resolvedType }) => !!contextValue || !!resolvedType),
                distinctUntilChanged(),
                mergeMap(({ contextValue, resolvedType }) => {
                    return resolveTabsOnPlatform(
                        messageBus,
                        outletPath,
                        contextValue,
                        resolvedType
                    );
                })
            )
            .subscribe(resolvedTabs$);

        const resolvedTabsSubscription = resolvedTabs$.subscribe(([resolvedTabs, authPayload]) => {
            const outletMinHeight = memorizedProps$.getValue().minHeight as string;
            // tslint:disable-next-line:no-console
            console.log(outletMinHeight);
            resolvedTabs.forEach(tab => {
                checkRequiredPermissionsOnPlatform(
                    messageBus,
                    (tab as TectonResolvedTab).requiredUserCapabilities
                ).then(allowed => {
                    if (allowed) {
                        let tabPaneEl = customElement.querySelector<ITectonTabPane>(
                            `tecton-tab-pane[value="${tab.tabLabel}"]`
                        );
                        if (tabPaneEl) {
                            const { label, value, name } = tabPaneEl;

                            const newTabPaneEl = buildTectonTabPane(
                                tab as TectonResolvedTab,
                                authPayload,
                                {
                                    label,
                                    name,
                                    value,
                                    outletMinHeight
                                }
                            );

                            tabPaneEl.parentElement!.replaceChild(newTabPaneEl, tabPaneEl);
                        } else {
                            tabPaneEl = buildTectonTabPane(tab as TectonResolvedTab, authPayload, {
                                outletMinHeight
                            });
                            customElement.querySelector('q2-tab-container')!.appendChild(tabPaneEl);
                        }

                        if (tab.moduleId) {
                            teardown$.next(function() {
                                sendMessage(
                                    'clearParams',
                                    Maybe.just(JSON.stringify({ moduleId: tab.moduleId }))
                                );
                            });
                        }
                    }
                });
            });
        });

        const buildTectonTabPane = (
            tab: TectonResolvedTab,
            authPayload: { key: string; value: string }[],
            options?: { label?: string; name?: string; value?: string; outletMinHeight?: string }
        ): ITectonTabPane => {
            const tabPaneEl = document.createElement('tecton-tab-pane') as ITectonTabPane;
            const label =
                (tab.tabLabelOverride && loc(tab.tabLabelOverride)) ||
                (options && options.label ? loc(options.label) : loc(tab.tabLabel));
            const value = options && options.value ? options.value : tab.tabLabel;
            let name = options && options.name;

            if (!name) {
                const container = customElement.querySelector<DSCustomElement>('q2-tab-container');
                name = container && container.name;
            }

            const loaderWrapper = document.createElement('div') as HTMLElement;
            loaderWrapper.style.minHeight = options?.outletMinHeight || '0px';
            let loaders = [];

            if (tab.loadingOptions) {
                loaders = buildLoaders(tab.loadingOptions);
                const outlet = tab.loadingOptions.outlet;
                outlet && outlet.style && loaderWrapper.setAttribute('style', outlet.style);
                outlet && outlet.classList && loaderWrapper.classList.add(...outlet.classList);
            } else {
                loaders = buildLoaders();
            }

            for (const loaderRow of loaders) {
                const row = document.createElement('div');
                row.className = 'q2-row';
                for (const loader of loaderRow) {
                    row.appendChild(loader);
                }
                loaderWrapper.appendChild(row);
            }
            loaderWrapper.slot = 'loading-wrapper';
            loaderWrapper.setAttribute('data-tecton-module', '');

            Object.assign(tabPaneEl, {
                label,
                value,
                name,
                provided: false,
                url: addParamToURL(tab.url, {
                    'tct-id': tab.moduleId,
                    'tct-ctxid': memorizedProps$.getValue().contextValue,
                    ...tab.initialParams,
                    'tct-maxHeight': customElement.style.maxHeight,
                    'tct-maxWidth': customElement.style.maxWidth
                }),
                moduleId: tab.moduleId,
                minHeight: tab.minHeight || (options && options.outletMinHeight),
                showForm: tab.formPostAuth,
                authPayload
            });

            tabPaneEl.innerHTML = '';
            tabPaneEl.appendChild(loaderWrapper);

            return tabPaneEl;
        };

        const tabSettled$ = new Subject<CustomEvent>();

        const observer = new MutationObserver(() => {
            const tabContainer = customElement.querySelector('q2-tab-container');
            if (tabContainer) {
                const settledSubscription = fromEvent(
                    tabContainer as FromEventTarget<CustomEvent>,
                    'settled'
                ).subscribe(event => {
                    tabSettled$.next(event);
                });

                teardown$.next(() => {
                    settledSubscription.unsubscribe();
                });

                observer.disconnect();
            }
        });

        observer.observe(customElement, {
            childList: true,
            subtree: true
        });

        teardown$.next(() => {
            observer.disconnect();
        });

        const effect$ = tabSettled$.pipe(
            debounceTime(100),
            map(e => {
                settled$.next(false);

                return () => {
                    settled$.next(true);
                };
            })
        );

        teardown$.next(() => {
            memorizedPropsSubscription.unsubscribe();
            resolvedTabsSubscription.unsubscribe();
        });

        return {
            DOM$: new BehaviorSubject(wrapperEl as HTMLElement),
            settled$,
            teardown$,
            effect$
        };
    };
}

export function resolveTabsOnPlatform(
    messageBus: IMessageBus,
    outletPath: string,
    contextValue: string,
    resolvedType: string
): Promise<[ResolvedTab[], { key: string; value: string }[]]> {
    const resolveTabsPromise = messageBus
        .sendRequest<{ resolvedTabs: ResolvedTab[] }>(
            'resolveTabs',
            Maybe.just(
                JSON.stringify({
                    outletPath,
                    contextValue,
                    resolvedType
                })
            )
        )
        .then(response => response.value.resolvedTabs);
    const authPayloadPromise = messageBus
        .sendRequest<{ key: string; value: string }[]>('getAuthPayload', Maybe.nothing())
        .then(r => r.value);
    return Promise.all([resolveTabsPromise, authPayloadPromise]);
}
